/**
 * Google reCAPTCHA v3 token helper (config-agnostic, framework-agnostic).
 *
 * reCAPTCHA v3 is invisible/score-based: there is no user challenge. You request
 * a short-lived token for a named `action` right before a protected request,
 * send it to the backend, and the server verifies it (score + action) with the
 * secret key.
 *
 * This lives in the shared package so every app can reuse it. It deliberately
 * does NOT read any app config — callers pass their own site key (typically via
 * a thin per-app wrapper). The script is injected lazily and only once (the load
 * promise is memoised per site key), so importing has no cost until first use.
 *
 * Usage (per-app wrapper):
 *   import config from '../config';
 *   import { getRecaptchaToken as exec } from '@app/http-client';
 *   export const getRecaptchaToken = (action) =>
 *     exec(config.recaptchaSiteKey, action);
 */

const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js?render=';

// Memoise per site key so different keys don't clobber each other.
const loaders = {};

function loadRecaptcha(siteKey) {
  if (loaders[siteKey]) return loaders[siteKey];

  loaders[siteKey] = new Promise((resolve, reject) => {
    // // const { grecaptcha } = window;

    // // Already injected (e.g. by an earlier call / another screen).
    // // if (grecaptcha && grecaptcha.execute) {
    // //   grecaptcha.ready(() => resolve(window.grecaptcha));
    // //   return;
    // // }

    const script = document.createElement('script');
    script.src = `${RECAPTCHA_SRC}${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => resolve(window.grecaptcha));
      } else {
        delete loaders[siteKey];
        reject(new Error('reCAPTCHA loaded but grecaptcha is unavailable'));
      }
    };
    script.onerror = () => {
      // Reset so a later call can retry.
      delete loaders[siteKey];
      reject(new Error('Failed to load reCAPTCHA script'));
    };
    document.head.appendChild(script);
  });

  return loaders[siteKey];
}

/**
 * Returns a fresh reCAPTCHA v3 token for `action`, or `null` when no site key is
 * provided (so callers can degrade gracefully). Tokens are single-use and expire
 * in ~2 minutes, so call this immediately before the request it protects.
 *
 * @param {string} siteKey - the reCAPTCHA v3 public site key.
 * @param {string} action - lowercase action name (e.g. 'ahc_package_lead').
 * @returns {Promise<string|null>}
 */
export async function getRecaptchaToken(siteKey, action) {
  if (!siteKey) return null;
  const grecaptcha = await loadRecaptcha(siteKey);
  return grecaptcha.execute(siteKey, { action });
}
